import { FC, ReactNode, useLayoutEffect } from 'react';
import { isAuthorizedAtom, sidfotContentAtom } from '@/lib/store/Contentstore';
import { useAtomValue } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import Adobe from '../tagManagement/Adobe';
import Driftmeddelanden from './Driftmeddelanden/Driftmeddelanden';
import Header from './Header/Header';
import Main from './Main/Main';
import Sidfot from './Sidfot/Sidfot';
import SmartFAQ from '../tagManagement/SmartFAQ';
import styles from './Layout.module.css';

const ScrollRestoration: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  return children;
};

const Layout: FC = (): ReactNode => {
  const nav = useNavigate();
  const isAuthorized = useAtomValue(isAuthorizedAtom);
  const sidfotContent = useAtomValue(sidfotContentAtom);

  const redirect = (e: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
    if (e.isDefaultPrevented()) return;

    const anchor = findParentByTagName(e.target as HTMLElement, 'A');
    if (anchor !== null && anchor instanceof HTMLAnchorElement) {
      if (anchor.target === '_blank' || anchor.href.length === 0 || anchor.download)
        return;

      const href = new URL(anchor.href);
      const isLocal = href.origin === document.location.origin;

      if (isLocal) {
        const fileExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.xlsm', '.ppt', '.pptx'];
        if (fileExtensions.some(ext => href.pathname.endsWith(`${ext}`))) {
          return;
        }
        e.preventDefault();
        nav(href.pathname);
      }
    }
  };

  const keydown = () => { };

  function findParentByTagName(element: HTMLElement | null, tagName: string) {
    let parent = element;

    while (parent !== null && parent.tagName !== tagName.toUpperCase()) {
      parent = parent.parentNode as HTMLElement;
    }

    return parent;
  }

  return (
    <div className={styles.container} onClick={redirect} onKeyDown={keydown} role="link" tabIndex={-1}>
      {isAuthorized && <SmartFAQ />}
      <Adobe />
      <Driftmeddelanden />
      <Header />
      <ScrollRestoration>
        <Main />
      </ScrollRestoration>
      {isAuthorized && sidfotContent && (
        <Sidfot sidfotResponse={sidfotContent} />
      )}
    </div>
  );
};

export default Layout;
